import { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { getContent } from "../lib/api";
import { HelpCircle, RefreshCw, Check, X as XIcon } from "lucide-react";

const CATS = ["pillars", "quran", "prophets", "calendar"];

function shuffle(a) { return [...a].sort(() => Math.random() - 0.5); }

export default function Quiz() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);

  useEffect(() => { getContent("quiz").then(setData); }, []);

  const start = (c) => {
    setCat(c);
    setQuestions(shuffle(data[c]));
    setIdx(0); setPicked(null); setScore(0);
  };

  const done = questions.length > 0 && idx >= questions.length;
  const q = questions[idx];

  const pick = (i) => {
    if (picked != null) return;
    setPicked(i);
    if (i === q.answer) setScore(s => s + 1);
  };

  const nextQ = () => { setPicked(null); setIdx(i => i + 1); };

  return (
    <div className="space-y-6 fade-in" data-testid="quiz-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><HelpCircle className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.quiz}</h1>
          {cat && <p className="text-sm text-nour-text2">{t.score} : {score}</p>}
        </div>
      </header>

      {!cat && (
        <div className="grid grid-cols-2 gap-3" data-testid="quiz-categories">
          {CATS.map(c => (
            <button key={c} onClick={() => data && start(c)} disabled={!data} className="nour-card p-6 text-left hover:bg-emerald-50 transition" data-testid={`quiz-cat-${c}`}>
              <div className="font-heading font-bold text-lg capitalize">{c}</div>
              <div className="text-xs text-nour-text2 mt-1">{data?.[c]?.length || 0} questions</div>
            </button>
          ))}
        </div>
      )}

      {cat && !done && q && (
        <article className="nour-card p-6" data-testid="quiz-question">
          <div className="text-xs text-nour-text2 uppercase tracking-wider">Question {idx + 1} / {questions.length}</div>
          <h2 className="font-heading text-xl font-bold mt-2 mb-5">{q.q_fr}</h2>
          <div className="space-y-2">
            {q.options.map((opt, i) => {
              const isCorrect = picked != null && i === q.answer;
              const isWrong = picked === i && i !== q.answer;
              return (
                <button
                  key={i}
                  onClick={() => pick(i)}
                  data-testid={`quiz-option-${i}`}
                  className={`w-full text-left p-4 rounded-xl border transition ${
                    isCorrect ? "bg-emerald-50 border-emerald-400" :
                    isWrong   ? "bg-red-50 border-red-400" :
                    picked != null ? "bg-white border-gray-200 opacity-60" :
                    "bg-white border-[rgba(29,158,117,0.2)] hover:bg-emerald-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{opt}</span>
                    {isCorrect && <Check className="w-4 h-4 text-emerald-600" />}
                    {isWrong && <XIcon className="w-4 h-4 text-red-600" />}
                  </div>
                </button>
              );
            })}
          </div>
          {picked != null && (
            <div className="mt-4 nour-card p-4 bg-amber-50">
              <p className="text-sm">{q.expl_fr}</p>
              <button className="btn-primary mt-3" onClick={nextQ} data-testid="quiz-next">{t.next}</button>
            </div>
          )}
        </article>
      )}

      {done && (
        <div className="nour-card p-8 text-center" data-testid="quiz-result">
          <div className="font-heading text-2xl font-bold">{t.final_score}</div>
          <div className="text-6xl font-extrabold text-nour-primary mt-2">{score}/{questions.length}</div>
          <p className="text-sm text-nour-text2 mt-3">{score >= questions.length * 0.7 ? t.great_job : t.keep_going}</p>
          <div className="mt-5 flex gap-3 justify-center">
            <button className="btn-primary" onClick={() => start(cat)} data-testid="quiz-replay"><RefreshCw className="w-4 h-4" /> {t.play_again}</button>
            <button className="btn-ghost" onClick={() => setCat(null)} data-testid="quiz-categories-back">{t.categories}</button>
          </div>
        </div>
      )}
    </div>
  );
}
